import React, {useEffect, useState} from 'react';
import '../../assets/css/Dentobook.css'
import '../../assets/css/GeneralForm.css'
import {Col, DatePicker, Form, Row, Select} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {CaretDownOutlined} from "@ant-design/icons";
import dayjs, {Dayjs} from "dayjs";
import {ClinicResponse, MedicalStaff, Patient, PatientListResponse} from "../../api-client";
import { isMobileOnly } from 'react-device-detect';

interface ReportsSearchFormProps {
    fromDate: Dayjs | undefined;
    setFromDate: Function;
    toDate: Dayjs | undefined;
    setToDate: Function;
    patient: string;
    setPatient: Function;
    doctor: string;
    setDoctor: Function;
    clinic: string;
    setClinic: Function;
    medicalStaff: MedicalStaff[];
    patients: PatientListResponse[];
    clinics: ClinicResponse[];
}

const ReportsSearchForm = ({
                               fromDate,
                               setFromDate,
                               toDate,
                               setToDate,
                               patient,
                               setPatient,
                               doctor,
                               setDoctor,
                               clinic,
                               setClinic,
                               patients,
                               medicalStaff,
                               clinics
                           }: ReportsSearchFormProps) => {
    const intl = useIntl();

    const [datesMode, setDatesMode] = useState<'today' | 'yesterday'
        | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'period'>('today');

    useEffect(() => {
        switch (datesMode) {
            case 'today':
                setFromDate(dayjs());
                setToDate(dayjs());

                break;
            case 'yesterday':
                const yesterdayDate = dayjs().subtract(1, 'days');
                setFromDate(yesterdayDate);
                setToDate(yesterdayDate);

                break;
            case 'this_week':
                setFromDate(dayjs().startOf('week'));
                setToDate(dayjs());

                break;
            case 'last_week':
                const oneWeekAgo = dayjs().subtract(1, 'weeks');
                setFromDate(oneWeekAgo.startOf('week'));
                setToDate(oneWeekAgo.endOf('week'));

                break;
            case 'this_month':
                setFromDate(dayjs().startOf('month'));
                setToDate(dayjs().endOf('month'));

                break;
            case 'last_month':
                const oneMonthAgo = dayjs().subtract(1, 'months');
                setFromDate(oneMonthAgo.startOf('month'));
                setToDate(oneMonthAgo.endOf('month'));

                break;
        }
    }, [datesMode]);

    return (
        <Form layout="vertical" className="mb-40">
            <Row gutter={[24, 7]}>
                <Col flex={4}>
                    <Form.Item
                        name="date"
                        label={' '}
                        className={"dentobook-form-item dentobook-centered-select input-full-width"}
                    >
                        <Select
                            size="large"
                            className="dentobook-input input-full-width"
                            optionFilterProp="label"
                            value={datesMode}
                            defaultValue={'today'}
                            onChange={(e: 'today' | 'yesterday'
                                | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'period') => {
                                setDatesMode(e);
                            }}
                            suffixIcon={<CaretDownOutlined/>}
                            options={[
                                {
                                    value: 'today',
                                    label: intl.formatMessage({id: 'today'}) + ', ' + dayjs().format('DD.MM.YYYY')
                                },
                                {
                                    value: 'yesterday',
                                    label: intl.formatMessage({id: 'yesterday'}) + ', ' + dayjs().subtract(1, 'days').format('DD.MM.YYYY')
                                },
                                {
                                    value: 'this_week',
                                    label: intl.formatMessage({id: 'this_week'})
                                },
                                {
                                    value: 'last_week',
                                    label: intl.formatMessage({id: 'last_week'})
                                },
                                {
                                    value: 'this_month',
                                    label: intl.formatMessage({id: 'this_month'})
                                },
                                {
                                    value: 'last_month',
                                    label: intl.formatMessage({id: 'last_month'})
                                },
                                {
                                    value: 'period',
                                    label: intl.formatMessage({id: 'period'})
                                }
                            ]}
                        />
                    </Form.Item>
                </Col>
                {datesMode === 'period' ?
                    <>
                        <Col flex={1}>
                            <Form.Item
                                className={"dentobook-form-item"}
                                name="from"
                                label={<FormattedMessage id={"from"}/>}
                            >
                                <DatePicker
                                    allowClear={false}
                                    defaultValue={fromDate}
                                    size={"large"}
                                    name={"from"}
                                    format={"DD.MM.YYYY"}
                                    onChange={e => {
                                        if (e) {
                                            setFromDate(e);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col flex={1}>
                            <Form.Item
                                className={"dentobook-form-item"}
                                name="to"
                                label={<FormattedMessage id={"to"}/>}
                            >
                                <DatePicker
                                    allowClear={false}
                                    defaultValue={toDate}
                                    size={"large"}
                                    name={"date"}
                                    className="input-ninety-nine-percent"
                                    format={"DD.MM.YYYY"}
                                    onChange={e => {
                                        if (e) {
                                            setToDate(e);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </>
                    : null}
                {clinics.length > 1 ?
                    <Col flex={datesMode === 'period' ? 9 : 10}>
                        <Form.Item
                            name="clinic"
                            label={isMobileOnly ? '' : ' '}
                            className={"dentobook-form-item dentobook-centered-select single-select"}
                        >
                            <Select
                                size="large"
                                className="dentobook-input"
                                optionFilterProp="label"
                                value={clinic}
                                defaultValue={''}
                                onChange={e => setClinic(e)}
                                suffixIcon={<CaretDownOutlined/>}
                                options={[
                                    {
                                        value: '',
                                        label: <FormattedMessage id="all_clinics"/>
                                    },
                                    ...clinics.map(c => ({
                                        value: c.id,
                                        label: c.name
                                    }))
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    : null}
                <Col flex={datesMode === 'period' ? 9 : 10}>
                    <Form.Item
                        name="doctor"
                        label={isMobileOnly ? '' : ' '}
                        className={"dentobook-form-item dentobook-centered-select single-select"}
                    >
                        <Select
                            size="large"
                            className="dentobook-input"
                            optionFilterProp="label"
                            value={doctor}
                            onChange={e => setDoctor(e)}
                            defaultValue={''}
                            suffixIcon={<CaretDownOutlined/>}
                            options={[
                                {
                                    value: '',
                                    label: <FormattedMessage id="doctors_all_label"/>
                                },
                                ...medicalStaff
                                    .filter(staff => staff.role === 'CLINIC_ADMIN' || staff.role === 'MEDICAL_DOCTOR')
                                    .map(staff => ({
                                        value: staff.id,
                                        label: staff.firstName + ' ' + staff.lastName
                                    }))
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col flex={datesMode === 'period' ? 9 : 10}>
                    <Form.Item
                        name="patient"
                        label={isMobileOnly ? '' : ' '}
                        className={"dentobook-form-item dentobook-centered-select single-select"}
                    >
                        <Select
                            size="large"
                            className="dentobook-input"
                            optionFilterProp="label"
                            value={patient}
                            defaultValue={''}
                            onChange={e => setPatient(e)}
                            suffixIcon={<CaretDownOutlined/>}
                            options={[
                                {
                                    value: '',
                                    label: <FormattedMessage id="patients_all_label"/>
                                },
                                ...patients.map(patient => ({
                                    value: patient.id,
                                    label: patient.firstName + ' ' + patient.lastName
                                }))
                            ]}
                        />
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};

export default ReportsSearchForm;
